import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {CheckCircleOutlined, DownloadOutlined, MailOutlined, ReloadOutlined, UploadOutlined} from "@ant-design/icons";
import {Button, Input, Select, Space, Table, Typography, App} from "antd";
import type {TableColumnsType} from "antd";
import {Link, useNavigate, useParams} from "react-router-dom";
import {shellBreadcrumbKeys, useShellBreadcrumbLabel} from "../../Contexts/ShellBreadcrumbContext";
import {useTranslation} from "react-i18next";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {AuthContext} from "../../Contexts/AuthContext";
import {
    buildParticipantOperationsCustomerPurchaseOrderDownloadUrl,
    buildParticipantOperationsCustomerPurchaseOrderUploadUrl,
    buildParticipantOperationsOfferDownloadUrl,
    buildParticipantOperationsOfferResendUrl,
    EMPTY_OPERATIONS_VALUE,
    operationalOfferStatusTone,
    operationalPaymentStatusTone
} from "./participantOperationsHelpers";
import {numberSorter, sortText, textSorter} from "./tableSorters";
import StripeOfferPaymentAction from "./StripeOfferPaymentAction";
import RecordPaymentDialog from "./RecordPaymentDialog";
import {DeletePopconfirm} from "../../Components/DeleteConfirmation";
import {formatMoney} from "../../format";
import {eventParticipantRoute} from "./eventWorkspaceRoutes";
import {humanizeEnumValue, normalizeEnumKey} from "../../i18n/labelHelpers";
import {formatLocalDate, formatLocalDateTime} from "./adminDateFormat";
import {authorizationHeaders} from "../../api/authorization";
import {downloadResponseFile} from "../../downloadFile";
import {StatusTag} from "../../Components/ui/StatusTag";
import {AdminPage, PageHeader, Panel} from "../../Components/ui";
import {
    PayerOfferHistoryQuery,
    PayerType,
    PayerPaymentAllocationsQuery,
    PayersPageQuery,
    useCreatePayerMutation,
    useDeletePayerMutation,
    useManualReconcilePayerPaymentMutation,
    usePayerOfferHistoryQuery,
    usePayerPaymentAllocationsQuery,
    usePayersPageQuery,
    useRenewPayerEventParticipationOfferMutation,
    useUpdatePayerMutation
} from "../../GraphQL/generated/graphql";

type PayerRecord = NonNullable<NonNullable<PayersPageQuery["payers"]>["items"]>[number];

type PayerOfferHistoryRecord = NonNullable<NonNullable<PayerOfferHistoryQuery["payerOfferHistory"]>["items"]>[number];

type PayerPaymentAllocationRecord = NonNullable<NonNullable<PayerPaymentAllocationsQuery["payerPaymentAllocations"]>["items"]>[number];

type PayerFormState = {
    id?: number;
    type: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    taxId: string;
    contactPerson: string;
};

const emptyPayerForm = (): PayerFormState => ({
    type: "INDIVIDUAL",
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "HR",
    taxId: "",
    contactPerson: ""
});

const toFormState = (payer: PayerRecord): PayerFormState => ({
    id: payer.id,
    type: payer.type?.trim() || "INDIVIDUAL",
    name: payer.name?.trim() || "",
    email: payer.email?.trim() || "",
    phone: payer.phone?.trim() || "",
    address: payer.address?.trim() || "",
    city: payer.city?.trim() || "",
    country: payer.country?.trim() || "",
    taxId: payer.taxId?.trim() || "",
    contactPerson: payer.contactPerson?.trim() || ""
});

const isIndividualPayerType = (type?: string | null) =>
    (type?.trim().toUpperCase() || "INDIVIDUAL") === "INDIVIDUAL";

const PayerFormField: FC<{
    controlId: string;
    label: string;
    children: React.ReactNode;
}> = ({controlId, label, children}) => (
    <div style={{display: "grid", gap: 6}}>
        <label htmlFor={controlId} style={{fontWeight: 600}}>
            {label}
        </label>
        {children}
    </div>
);

const toInput = (form: PayerFormState) => ({
    type: (form.type.trim() || PayerType.Individual) as PayerType,
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    address: form.address.trim(),
    city: form.city.trim(),
    country: form.country.trim(),
    taxId: form.taxId.trim(),
    contactPerson: isIndividualPayerType(form.type) ? "" : form.contactPerson.trim()
});

const enumLabel = (value?: string | null) => {
    const normalized = value?.trim().toUpperCase();

    if (normalized === "COMPANY") {
        return "Tvrtka";
    }

    if (normalized === "SPONSOREDPARTICIPATION" || normalized === "SPONSORED_PARTICIPATION") {
        return "Sponzor";
    }

    return "Osoba";
};

const isRenewableParticipationOffer = (offer: PayerOfferHistoryRecord) =>
    normalizeEnumKey(offer.type) === "PARTICIPATION" &&
    normalizeEnumKey(offer.status) === "EXPIRED";

const isManualReconcileEligible = (allocation: PayerPaymentAllocationRecord) => {
    const method = normalizeEnumKey(allocation.paymentMethod);
    const status = normalizeEnumKey(allocation.paymentStatus);
    const paymentId = Number(allocation.paymentId);

    return Number.isFinite(paymentId)
        && paymentId > 0
        && ["BANK_TRANSFER", "POS", "CASH", "SPONSOR"].includes(method)
        && !["PAID", "MANUALLY_RECONCILED", "REFUNDED", "CANCELLED"].includes(status);
};

const PayersPage: FC = () => {
    const {message, modal} = App.useApp();
    const {t} = useTranslation();
    const enumText = useCallback((value?: string | null) =>
        value ? t(`enums:${value}`, {defaultValue: humanizeEnumValue(value)}) : EMPTY_OPERATIONS_VALUE, [t]);
    const {auth} = useContext(AuthContext);
    const navigate = useNavigate();
    const {payerId} = useParams();
    const errorLabels = buildUserFacingErrorLabels(t);
    const routePayerId = Number(payerId);
    const selectedRoutePayerId = Number.isInteger(routePayerId) && routePayerId > 0 ? routePayerId : undefined;
    const isCreatingPayer = payerId?.trim().toLowerCase() === "new";
    const isPayerDetailRoute = isCreatingPayer || !!selectedRoutePayerId;
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [form, setForm] = useState<PayerFormState>(() => emptyPayerForm());
    const payersQuery = usePayersPageQuery({
        variables: {search: search.trim()},
        fetchPolicy: "cache-and-network"
    });
    const payerOfferHistoryQuery = usePayerOfferHistoryQuery({
        variables: {payerId: selectedRoutePayerId ?? 0},
        skip: !selectedRoutePayerId,
        fetchPolicy: "cache-and-network"
    });
    const payerPaymentAllocationsQuery = usePayerPaymentAllocationsQuery({
        variables: {payerId: selectedRoutePayerId ?? 0},
        skip: !selectedRoutePayerId,
        fetchPolicy: "cache-and-network"
    });
    const [createPayer, createState] = useCreatePayerMutation();
    const [updatePayer, updateState] = useUpdatePayerMutation();
    const [deletePayer, deleteState] = useDeletePayerMutation();
    const [renewEventParticipationOffer] = useRenewPayerEventParticipationOfferMutation();
    const [manualReconcilePayment] = useManualReconcilePayerPaymentMutation();
    const [downloadingOfferId, setDownloadingOfferId] = useState<number>();
    const [downloadingPurchaseOrderOfferId, setDownloadingPurchaseOrderOfferId] = useState<number>();
    const [uploadingPurchaseOrderOfferId, setUploadingPurchaseOrderOfferId] = useState<number>();
    const [resendingOfferId, setResendingOfferId] = useState<number>();
    const [renewingOfferId, setRenewingOfferId] = useState<number>();
    const [recordingPayment, setRecordingPayment] = useState(false);
    const [reconcilingPaymentId, setReconcilingPaymentId] = useState<number>();
    const saving = createState.loading || updateState.loading;
    const payers = useMemo(() => (payersQuery.data?.payers?.items ?? [])
        .filter((item): item is PayerRecord => !!item), [payersQuery.data?.payers?.items]);
    const payerOffers = useMemo(() => (payerOfferHistoryQuery.data?.payerOfferHistory?.items ?? [])
        .filter((item): item is PayerOfferHistoryRecord => !!item), [payerOfferHistoryQuery.data?.payerOfferHistory?.items]);
    const payerPaymentAllocations = useMemo(() => (payerPaymentAllocationsQuery.data?.payerPaymentAllocations?.items ?? [])
        .filter((item): item is PayerPaymentAllocationRecord => !!item), [
        payerPaymentAllocationsQuery.data?.payerPaymentAllocations?.items
    ]);
    const selectedPayer = useMemo(() =>
        selectedRoutePayerId ? payers.find(payer => payer.id === selectedRoutePayerId) : undefined,
    [payers, selectedRoutePayerId]);
    useShellBreadcrumbLabel(selectedRoutePayerId ? shellBreadcrumbKeys.payer(selectedRoutePayerId) : undefined, selectedPayer?.name);

    useEffect(() => {
        if (isCreatingPayer) {
            setForm(emptyPayerForm());
        } else if (selectedPayer) {
            setForm(toFormState(selectedPayer));
        } else if (!selectedRoutePayerId) {
            setForm(emptyPayerForm());
        }
    }, [
        isCreatingPayer,
        selectedPayer?.address,
        selectedPayer?.city,
        selectedPayer?.contactPerson,
        selectedPayer?.country,
        selectedPayer?.email,
        selectedPayer?.id,
        selectedPayer?.name,
        selectedPayer?.phone,
        selectedPayer?.taxId,
        selectedPayer?.type,
        selectedRoutePayerId
    ]);

    const columns = useMemo<TableColumnsType<PayerRecord>>(() => [
        {
            title: t("payer-name", {defaultValue: "Name / person"}),
            dataIndex: "name",
            key: "name",
            sorter: textSorter(payer => sortText(payer.name, payer.type)),
            render: (_, payer) => (
                <Space direction="vertical" size={0}>
                    <Link className="ati-table-link" to={`/Admin/Payers/${payer.id}`}>{payer.name || `#${payer.id}`}</Link>
                    <Typography.Text type="secondary">{enumLabel(payer.type)}</Typography.Text>
                </Space>
            )
        },
        {
            title: t("payer-email", {defaultValue: "Payer email"}),
            dataIndex: "email",
            key: "email",
            sorter: textSorter(payer => payer.email)
        },
        {
            title: t("payer-tax-id", {defaultValue: "Payer OIB"}),
            dataIndex: "taxId",
            key: "taxId",
            sorter: textSorter(payer => payer.taxId)
        }
    ], [t]);

    const updateForm = useCallback((field: keyof PayerFormState, value: string) => {
        setForm(current => ({
            ...current,
            [field]: value
        }));
    }, []);

    const refetchPayerOffers = useCallback(async () => {
        if (selectedRoutePayerId) {
            await payerOfferHistoryQuery.refetch();
        }
    }, [payerOfferHistoryQuery, selectedRoutePayerId]);

    const refetchPayerPaymentAllocations = useCallback(async () => {
        if (selectedRoutePayerId) {
            await payerPaymentAllocationsQuery.refetch();
        }
    }, [payerPaymentAllocationsQuery, selectedRoutePayerId]);

    const handleOfferDownload = useCallback(async (offer: PayerOfferHistoryRecord) => {
        setDownloadingOfferId(offer.id);

        try {
            const response = await fetch(buildParticipantOperationsOfferDownloadUrl(offer.eventId, offer.id), {
                credentials: "include",
                headers: authorizationHeaders(auth.token)
            });

            if (!response.ok) {
                throw new Error("Download failed");
            }

            await downloadResponseFile(response, offer.fileName?.trim() || "offer.pdf");
        } catch {
            message.error(t("error.download-failed", {defaultValue: "Download failed"}));
        } finally {
            setDownloadingOfferId(undefined);
        }
    }, [message, auth.token, t]);

    const handleCustomerPurchaseOrderDownload = useCallback(async (offer: PayerOfferHistoryRecord) => {
        setDownloadingPurchaseOrderOfferId(offer.id);

        try {
            const response = await fetch(buildParticipantOperationsCustomerPurchaseOrderDownloadUrl(offer.eventId, offer.id), {
                credentials: "include",
                headers: authorizationHeaders(auth.token)
            });

            if (!response.ok) {
                throw new Error("Download failed");
            }

            await downloadResponseFile(response, offer.customerPurchaseOrderFileName?.trim() || "narudzbenica-kupca.pdf");
        } catch {
            message.error(t("customer-purchase-order-download-failed", {
                defaultValue: "Customer purchase order could not be downloaded."
            }));
        } finally {
            setDownloadingPurchaseOrderOfferId(undefined);
        }
    }, [message, auth.token, t]);

    const handleCustomerPurchaseOrderUpload = useCallback(async (
        offer: PayerOfferHistoryRecord,
        file?: File | null
    ) => {
        if (!file) {
            return;
        }

        setUploadingPurchaseOrderOfferId(offer.id);

        try {
            const body = new FormData();
            body.append("file", file);

            const response = await fetch(buildParticipantOperationsCustomerPurchaseOrderUploadUrl(offer.eventId, offer.id), {
                method: "POST",
                credentials: "include",
                headers: authorizationHeaders(auth.token),
                body
            });

            if (!response.ok) {
                throw new Error("Upload failed");
            }

            await refetchPayerOffers();
            message.success(t("customer-purchase-order-upload-success", {
                defaultValue: "Customer purchase order saved."
            }));
        } catch {
            message.error(t("customer-purchase-order-upload-failed", {
                defaultValue: "Customer purchase order could not be saved."
            }));
        } finally {
            setUploadingPurchaseOrderOfferId(undefined);
        }
    }, [message, auth.token, refetchPayerOffers, t]);

    const handleOfferResend = useCallback(async (offer: PayerOfferHistoryRecord) => {
        setResendingOfferId(offer.id);

        try {
            const response = await fetch(buildParticipantOperationsOfferResendUrl(offer.eventId, offer.id), {
                method: "POST",
                credentials: "include",
                headers: authorizationHeaders(auth.token)
            });

            if (!response.ok) {
                throw new Error("Resend failed");
            }

            message.success(t("offer-resend-queued", {defaultValue: "Offer email queued."}));
            await refetchPayerOffers();
        } catch {
            message.error(t("offer-resend-failed", {defaultValue: "Offer email could not be sent"}));
        } finally {
            setResendingOfferId(undefined);
        }
    }, [message, auth.token, refetchPayerOffers, t]);

    const handleParticipationOfferRenew = useCallback(async (offer: PayerOfferHistoryRecord) => {
        if (!isRenewableParticipationOffer(offer)) {
            return;
        }

        const confirmed = await modal.confirm({
            title: t("renew-participation-offer-confirm", {
                defaultValue: "Replace this expired participation offer using currently valid prices?"
            }),
            okText: t("confirm", {defaultValue: "Confirm"}),
            cancelText: t("cancel", {defaultValue: "Cancel"})
        });

        if (!confirmed) {
            return;
        }

        setRenewingOfferId(offer.id);

        try {
            await renewEventParticipationOffer({
                variables: {
                    input: {
                        eventId: offer.eventId,
                        offerId: offer.id
                    }
                }
            });
            await refetchPayerOffers();
            message.success(t("participation-offer-renew-success", {
                defaultValue: "Replacement offer sent."
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participation-offer-renew-failed", {
                    defaultValue: "Replacement offer could not be sent."
                })
            }));
        } finally {
            setRenewingOfferId(undefined);
        }
    }, [message, modal, errorLabels, refetchPayerOffers, renewEventParticipationOffer, t]);

    const handlePaymentManualReconcile = useCallback(async (allocation: PayerPaymentAllocationRecord) => {
        if (!isManualReconcileEligible(allocation)) {
            return;
        }

        const note = window.prompt(t("manual-reconcile-note-prompt", {
            defaultValue: "Enter reconciliation note."
        }), "")?.trim();

        if (note === undefined) {
            return;
        }

        if (!note) {
            message.error(t("manual-reconcile-note-required", {
                defaultValue: "Reconciliation note is required."
            }));
            return;
        }

        setReconcilingPaymentId(allocation.paymentId);

        try {
            await manualReconcilePayment({
                variables: {
                    input: {
                        id: allocation.paymentId,
                        note
                    }
                }
            });
            await refetchPayerPaymentAllocations();
            message.success(t("manual-reconcile-success", {
                defaultValue: "Payment marked as reconciled."
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("manual-reconcile-failed", {
                    defaultValue: "Payment could not be marked as reconciled."
                })
            }));
        } finally {
            setReconcilingPaymentId(undefined);
        }
    }, [message, errorLabels, manualReconcilePayment, refetchPayerPaymentAllocations, t]);

    const handleNew = useCallback(() => {
        navigate("/Admin/Payers/new");
        setForm(emptyPayerForm());
    }, [navigate]);

    const handleSave = useCallback(async () => {
        try {
            if (form.id) {
                await updatePayer({
                    variables: {
                        input: {
                            id: form.id,
                            ...toInput(form)
                        }
                    }
                });
                message.success(t("payer-save-success", {defaultValue: "Payer saved."}));
            } else {
                const result = await createPayer({
                    variables: {
                        input: toInput(form)
                    }
                });
                const created = result.data?.createPayer?.[0];
                if (created?.id) {
                    navigate(`/Admin/Payers/${created.id}`);
                }
                message.success(t("payer-create-success", {defaultValue: "Payer added."}));
            }

            await payersQuery.refetch();
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("payer-save-failed", {defaultValue: "The payer could not be saved."})
            }));
        }
    }, [message, createPayer, errorLabels, form, navigate, payersQuery, t, updatePayer]);

    const handleDelete = useCallback(async () => {
        if (!form.id) {
            return;
        }

        try {
            await deletePayer({
                variables: {
                    input: {
                        id: form.id
                    }
                }
            });
            message.success(t("payer-delete-success", {defaultValue: "Payer deleted."}));
            navigate("/Admin/Payers");
            setForm(emptyPayerForm());
            await payersQuery.refetch();
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("payer-delete-failed", {defaultValue: "The payer could not be deleted."})
            }));
        }
    }, [message, deletePayer, errorLabels, form.id, navigate, payersQuery, t]);

    const offerColumns = useMemo<TableColumnsType<PayerOfferHistoryRecord>>(() => [
        {
            key: "offer",
            title: t("offer", {defaultValue: "Offer"}),
            sorter: textSorter(offer => sortText(offer.offerNumber, offer.type, offer.paymentMethod)),
            render: (_, offer) => (
                <Space direction="vertical" size={2}>
                    <Space size={4} wrap>
                        <Typography.Text strong>
                            {offer.offerNumber?.trim() || EMPTY_OPERATIONS_VALUE}
                        </Typography.Text>
                        {offer.type ? <StatusTag tone="info">{enumText(offer.type)}</StatusTag> : null}
                    </Space>
                    <Typography.Text type="secondary">
                        {enumText(offer.paymentMethod)}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "participant",
            title: t("participant-and-event", {defaultValue: "Participant / event"}),
            sorter: textSorter(offer => sortText(
                offer.participantFullName,
                offer.eventName,
                offer.eventCode
            )),
            render: (_, offer) => (
                <Space direction="vertical" size={2}>
                    <Link
                        to={eventParticipantRoute(offer.eventId, offer.participantId)}
                        state={{fallbackName: offer.participantFullName?.trim()}}
                    >
                        {offer.participantFullName?.trim() || EMPTY_OPERATIONS_VALUE}
                    </Link>
                    <Typography.Text type="secondary">
                        {offer.eventName?.trim() || offer.eventCode?.trim() || EMPTY_OPERATIONS_VALUE}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "status",
            title: t("status", {defaultValue: "Status"}),
            sorter: textSorter(offer => sortText(offer.status, offer.dueDate, offer.generatedAt)),
            render: (_, offer) => (
                <Space direction="vertical" size={2}>
                    {offer.status ? (
                        <StatusTag tone={operationalOfferStatusTone(offer.status)}>
                            {enumText(offer.status)}
                        </StatusTag>
                    ) : EMPTY_OPERATIONS_VALUE}
                    <Typography.Text type="secondary">
                        {t("due-date", {defaultValue: "Due date"})}: {formatLocalDate(offer.dueDate, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("generated", {defaultValue: "Generated"})}: {formatLocalDateTime(offer.generatedAt, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("queued", {defaultValue: "Queued"})}: {formatLocalDateTime(offer.emailQueuedAtUtc, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("emailed", {defaultValue: "Emailed"})}: {formatLocalDateTime(offer.emailedAt, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    {offer.emailFailureReason?.trim() ? (
                        <Typography.Text type="danger">
                            {offer.emailFailureReason}
                        </Typography.Text>
                    ) : null}
                </Space>
            )
        },
        {
            key: "pdf",
            title: t("offer-document", {defaultValue: "Offer document"}),
            sorter: textSorter(offer => offer.fileId ? "ready" : "missing"),
            render: (_, offer) => (
                <StatusTag tone={offer.fileId ? "info" : "neutral"}>
                    {offer.fileId
                        ? t("document-ready", {defaultValue: "Available"})
                        : t("document-unavailable", {defaultValue: "Unavailable"})}
                </StatusTag>
            )
        },
        {
            key: "customerPurchaseOrder",
            title: t("customer-purchase-order", {defaultValue: "Customer purchase order"}),
            sorter: textSorter(offer => sortText(
                offer.customerPurchaseOrderFileName,
                offer.customerPurchaseOrderFileId ? "ready" : "missing"
            )),
            render: (_, offer) => {
                const inputId = `customer-purchase-order-${offer.id}`;
                const hasPurchaseOrder = !!offer.customerPurchaseOrderFileId;

                return (
                    <Space direction="vertical" size={6}>
                        {hasPurchaseOrder ? (
                            <Button
                                size="small"
                                icon={<DownloadOutlined/>}
                                loading={downloadingPurchaseOrderOfferId === offer.id}
                                onClick={() => handleCustomerPurchaseOrderDownload(offer)}
                            >
                                {offer.customerPurchaseOrderFileName?.trim()
                                    || t("download-customer-purchase-order", {defaultValue: "Download purchase order"})}
                            </Button>
                        ) : (
                            <StatusTag>{t("customer-purchase-order-missing", {defaultValue: "Missing"})}</StatusTag>
                        )}
                        <input
                            id={inputId}
                            aria-label={t("customer-purchase-order", {defaultValue: "Customer purchase order"})}
                            type="file"
                            style={{display: "none"}}
                            onChange={event => {
                                const file = event.currentTarget.files?.[0];
                                event.currentTarget.value = "";
                                void handleCustomerPurchaseOrderUpload(offer, file);
                            }}
                        />
                        <Button
                            size="small"
                            icon={<UploadOutlined/>}
                            loading={uploadingPurchaseOrderOfferId === offer.id}
                            onClick={() => document.getElementById(inputId)?.click()}
                        >
                            {hasPurchaseOrder
                                ? t("replace-customer-purchase-order", {defaultValue: "Replace purchase order"})
                                : t("upload-customer-purchase-order", {defaultValue: "Upload purchase order"})}
                        </Button>
                    </Space>
                );
            }
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, offer) => (
                <Space size={8} wrap>
                    {normalizeEnumKey(offer.paymentMethod) === "CARD" &&
                        !["REPLACED", "EXPIRED"].includes(normalizeEnumKey(offer.status)) &&
                        <StripeOfferPaymentAction eventId={offer.eventId} offerId={offer.id}/>}
                    <Button
                        size="small"
                        icon={<DownloadOutlined/>}
                        loading={downloadingOfferId === offer.id}
                        disabled={!offer.fileId}
                        onClick={() => handleOfferDownload(offer)}
                    >
                        {t("download", {defaultValue: "Download"})}
                    </Button>
                    <Button
                        size="small"
                        icon={<MailOutlined/>}
                        loading={resendingOfferId === offer.id}
                        disabled={normalizeEnumKey(offer.status) === "QUEUED"}
                        onClick={() => handleOfferResend(offer)}
                    >
                        {t("resend", {defaultValue: "Resend"})}
                    </Button>
                    <Button
                        size="small"
                        icon={<ReloadOutlined/>}
                        loading={renewingOfferId === offer.id}
                        disabled={!isRenewableParticipationOffer(offer)}
                        onClick={() => handleParticipationOfferRenew(offer)}
                    >
                        {t("replace-expired-offer", {defaultValue: "Replace expired offer"})}
                    </Button>
                </Space>
            )
        }
    ], [
        downloadingPurchaseOrderOfferId,
        downloadingOfferId,
        handleCustomerPurchaseOrderDownload,
        handleCustomerPurchaseOrderUpload,
        handleOfferDownload,
        handleOfferResend,
        handleParticipationOfferRenew,
        renewingOfferId,
        resendingOfferId,
        uploadingPurchaseOrderOfferId,
        t
    ]);

    const paymentAllocationColumns = useMemo<TableColumnsType<PayerPaymentAllocationRecord>>(() => [
        {
            key: "allocation",
            title: t("payment-allocation", {defaultValue: "Payment allocation"}),
            sorter: textSorter(allocation => sortText(
                allocation.description,
                allocation.offerNumber,
                allocation.reference
            )),
            render: (_, allocation) => (
                <Space direction="vertical" size={2}>
                    <Typography.Text>{allocation.description?.trim() || EMPTY_OPERATIONS_VALUE}</Typography.Text>
                    <Typography.Text type="secondary">
                        {allocation.offerNumber?.trim()
                            ? t("offer-number-value", {
                                offerNumber: allocation.offerNumber,
                                defaultValue: `Ponuda ${allocation.offerNumber}`
                            })
                            : t("payment-reference-value", {
                                reference: allocation.reference?.trim() || EMPTY_OPERATIONS_VALUE,
                                defaultValue: `Poziv ${allocation.reference?.trim() || EMPTY_OPERATIONS_VALUE}`
                            })}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "participant",
            title: t("participant-and-event", {defaultValue: "Participant / event"}),
            sorter: textSorter(allocation => sortText(
                allocation.participantFullName,
                allocation.eventName,
                allocation.eventCode
            )),
            render: (_, allocation) => (
                <Space direction="vertical" size={2}>
                    <Typography.Text>{allocation.participantFullName?.trim() || EMPTY_OPERATIONS_VALUE}</Typography.Text>
                    <Typography.Text type="secondary">
                        {allocation.eventName?.trim() || allocation.eventCode?.trim() || EMPTY_OPERATIONS_VALUE}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "status",
            title: t("payment-state", {defaultValue: "Payment state"}),
            sorter: textSorter(allocation => sortText(allocation.paymentStatus, allocation.paymentMethod)),
            render: (_, allocation) => (
                <Space direction="vertical" size={2}>
                    {allocation.paymentStatus ? (
                        <StatusTag tone={operationalPaymentStatusTone(allocation.paymentStatus)}>
                            {enumText(allocation.paymentStatus)}
                        </StatusTag>
                    ) : EMPTY_OPERATIONS_VALUE}
                    <Typography.Text type="secondary">
                        {enumText(allocation.paymentMethod)}
                    </Typography.Text>
                    {allocation.manuallyReconciledAt ? (
                        <Typography.Text type="secondary">
                            {t("reconciled", {defaultValue: "Reconciled"})}: {formatLocalDateTime(allocation.manuallyReconciledAt, EMPTY_OPERATIONS_VALUE)}
                        </Typography.Text>
                    ) : null}
                </Space>
            )
        },
        {
            key: "amount",
            title: t("amount", {defaultValue: "Amount"}),
            sorter: numberSorter(allocation => allocation.allocatedAmount),
            render: (_, allocation) => (
                <Space direction="vertical" size={2}>
                    <Typography.Text>
                        {t("allocated-amount", {defaultValue: "Allocated"})}: {formatMoney(allocation.allocatedAmount, allocation.currency, null, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("payment-total", {defaultValue: "Payment total"})}: {formatMoney(allocation.paymentAmountGross, allocation.currency, null, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    {Number(allocation.paymentRefundedAmountGross) > 0 && <Typography.Text type="secondary">
                        {t("stripe-refunded")}: {formatMoney(allocation.paymentRefundedAmountGross, allocation.currency, null, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>}
                    <Typography.Text type="secondary">
                        {t("paid-at", {defaultValue: "Paid at"})}: {formatLocalDateTime(allocation.paidAt, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, allocation) => (
                <Button
                    size="small"
                    icon={<CheckCircleOutlined/>}
                    loading={reconcilingPaymentId === allocation.paymentId}
                    disabled={!isManualReconcileEligible(allocation)}
                    onClick={() => handlePaymentManualReconcile(allocation)}
                >
                    {t("mark-reconciled", {defaultValue: "Mark reconciled"})}
                </Button>
            )
        }
    ], [handlePaymentManualReconcile, reconcilingPaymentId, t]);

    const payerList = (
        <section>
            <div className="ati-stack ati-stack--compact">
                <Input.Search
                    allowClear
                    value={searchInput}
                    onChange={event => {
                        setSearchInput(event.target.value);
                        if (!event.target.value) {
                            setSearch("");
                        }
                    }}
                    onSearch={value => setSearch(value)}
                    placeholder={t("payer-search-placeholder", {
                        defaultValue: "Search by name, email or OIB"
                    })}
                />
                <Table<PayerRecord>
                    rowKey="id"
                    size="middle"
                    loading={payersQuery.loading}
                    columns={columns}
                    dataSource={payers}
                    pagination={{pageSize: 12}}
                    rowClassName={payer => payer.id === form.id ? "ant-table-row-selected" : ""}
                />
            </div>
        </section>
    );

    const payerForm = (
        <section>
            <div className="ati-stack ati-stack--compact">
                <Typography.Title level={4} style={{margin: 0}}>
                    {form.id
                        ? t("payer-edit", {defaultValue: "Edit payer"})
                        : t("payer-create", {defaultValue: "Add payer"})}
                </Typography.Title>
                <PayerFormField
                    controlId="payer-type"
                    label={t("payer-type", {defaultValue: "Payer type"})}
                >
                    <Select
                        id="payer-type"
                        style={{width: "100%"}}
                        value={form.type}
                        onChange={value => updateForm("type", String(value))}
                        options={[
                            {value: "INDIVIDUAL", label: t("payer-type-individual", {defaultValue: "Individual"})},
                            {value: "COMPANY", label: t("payer-type-company", {defaultValue: "Company"})},
                            {value: "SPONSORED_PARTICIPATION", label: t("payer-type-sponsor", {defaultValue: "Sponsor"})}
                        ]}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-name" label={t("payer-name", {defaultValue: "Name / person"})}>
                    <Input
                        id="payer-name"
                        value={form.name}
                        onChange={event => updateForm("name", event.target.value)}
                        placeholder={t("payer-name", {defaultValue: "Name / person"})}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-email" label={t("payer-email", {defaultValue: "Payer email"})}>
                    <Input
                        id="payer-email"
                        value={form.email}
                        onChange={event => updateForm("email", event.target.value)}
                        placeholder={t("payer-email", {defaultValue: "Payer email"})}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-phone" label={t("payer-phone", {defaultValue: "Payer phone"})}>
                    <Input
                        id="payer-phone"
                        value={form.phone}
                        onChange={event => updateForm("phone", event.target.value)}
                        placeholder={t("payer-phone", {defaultValue: "Payer phone"})}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-address" label={t("payer-address", {defaultValue: "Payer address"})}>
                    <Input
                        id="payer-address"
                        value={form.address}
                        onChange={event => updateForm("address", event.target.value)}
                        placeholder={t("payer-address", {defaultValue: "Payer address"})}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-city" label={t("payer-city", {defaultValue: "Payer city"})}>
                    <Input
                        id="payer-city"
                        value={form.city}
                        onChange={event => updateForm("city", event.target.value)}
                        placeholder={t("payer-city", {defaultValue: "Payer city"})}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-country" label={t("payer-country", {defaultValue: "Payer country"})}>
                    <Input
                        id="payer-country"
                        value={form.country}
                        onChange={event => updateForm("country", event.target.value)}
                        placeholder={t("payer-country", {defaultValue: "Payer country"})}
                    />
                </PayerFormField>
                <PayerFormField controlId="payer-tax-id" label={t("payer-tax-id", {defaultValue: "Payer OIB"})}>
                    <Input
                        id="payer-tax-id"
                        value={form.taxId}
                        onChange={event => updateForm("taxId", event.target.value)}
                        placeholder={t("payer-tax-id", {defaultValue: "Payer OIB"})}
                    />
                </PayerFormField>
                {!isIndividualPayerType(form.type) && (
                    <PayerFormField
                        controlId="payer-contact-person"
                        label={t("payer-contact-person", {defaultValue: "Contact person"})}
                    >
                        <Input
                            id="payer-contact-person"
                            value={form.contactPerson}
                            onChange={event => updateForm("contactPerson", event.target.value)}
                            placeholder={t("payer-contact-person", {defaultValue: "Contact person"})}
                        />
                    </PayerFormField>
                )}
                <Space wrap>
                    <Button type="primary" loading={saving} onClick={handleSave}>
                        {t("save", {defaultValue: "Save"})}
                    </Button>
                    <DeletePopconfirm
                        itemType={t("delete-target-payer", {defaultValue: "payer"})}
                        itemName={form.name || form.email || form.id}
                        onConfirm={handleDelete}
                        disabled={!form.id}
                    >
                        <Button danger loading={deleteState.loading} disabled={!form.id}>
                            {t("delete", {defaultValue: "Delete"})}
                        </Button>
                    </DeletePopconfirm>
                </Space>
            </div>
        </section>
    );

    return (
        <AdminPage>
            <PageHeader
                title={t("payers", {defaultValue: "Payers"})}
                actions={(
                    <>
                        <Button type="primary" onClick={handleNew}>
                            {t("new-payer", {defaultValue: "New payer"})}
                        </Button>
                    </>
                )}
            />
            <div className="ati-stack">
                {isPayerDetailRoute ? payerForm : payerList}

                {selectedRoutePayerId ? (
                    <>
                        <Panel title={t("offer-history", {defaultValue: "Offer history"})}>
                            <Table<PayerOfferHistoryRecord>
                                rowKey="id"
                                size="middle"
                                loading={payerOfferHistoryQuery.loading}
                                columns={offerColumns}
                                dataSource={payerOffers}
                                pagination={false}
                                scroll={{x: 980}}
                                locale={{
                                    emptyText: t("no-stored-offers", {defaultValue: "No saved offers"})
                                }}
                            />
                        </Panel>

                        <Panel
                            title={t("payment-reconciliation", {defaultValue: "Payment reconciliation"})}
                            extra={(
                                <Button type="primary" onClick={() => setRecordingPayment(true)} disabled={payerOfferHistoryQuery.loading}>
                                    {t("payment-entry-title")}
                                </Button>
                            )}
                        >
                            {recordingPayment && <RecordPaymentDialog key={selectedRoutePayerId} payerId={selectedRoutePayerId}
                                offers={payerOffers} onClose={() => setRecordingPayment(false)}
                                onSaved={async () => {await Promise.all([payerPaymentAllocationsQuery.refetch(), payerOfferHistoryQuery.refetch()]);}}/>}
                            <Table<PayerPaymentAllocationRecord>
                                rowKey="id"
                                size="middle"
                                loading={payerPaymentAllocationsQuery.loading}
                                columns={paymentAllocationColumns}
                                dataSource={payerPaymentAllocations}
                                pagination={false}
                                scroll={{x: 980}}
                                locale={{
                                    emptyText: t("no-payment-allocations", {defaultValue: "No payment allocations"})
                                }}
                            />
                        </Panel>
                    </>
                ) : null}
            </div>
        </AdminPage>
    );
};

export default PayersPage;
